import { useMedia } from 'react-use';

import { varMappings } from '@/utils/generated/varMappings';

import { CHART_DIMENSIONS } from './constants';

const { BASE_WIDTH, BASE_HEIGHT } = CHART_DIMENSIONS;

// d3-free replacement for the removed d3/visx LoadingLineChart: it animated
// by phase-shifting a sine wave, which is the same as translating it
// horizontally, so a precomputed two-period path scrolled with SMIL
// reproduces it without shipping d3 in the chunks that render loading
// skeletons. Path generated with the original's scale math (d3-shape
// curveMonotoneX, y = sin(2*PI*i/25) + 7, y domain [0, 13]) over samples
// -1..50 at 776x378; one period spans 776 * 25/24 px, and the original
// shifted 0.15 samples per 20ms, so a full period takes 3.33s.
const PERIOD_PX = (BASE_WIDTH * 25) / 24;
const PERIOD_SECONDS = 3.33;

const LINE_PATH =
  'M-32.33,181.69C-21.56,179.28,-10.78,176.87,0,174.46C10.78,172.05,21.56,169.57,32.33,167.23C43.11,164.9,53.89,162.57,64.67,160.45C75.44,158.34,86.22,156.31,97,154.56C107.78,152.8,118.56,151.2,129.33,149.91C140.11,148.62,150.89,147.55,161.67,146.81C172.44,146.06,183.22,145.44,194,145.44C204.78,145.44,215.56,145.6,226.33,145.9C237.11,146.21,247.89,147.13,258.67,148.15C269.44,149.18,280.22,150.52,291,152.06C301.78,153.59,312.56,155.42,323.33,157.37C334.11,159.32,344.89,161.52,355.67,163.76C366.44,166,377.22,168.43,388,170.82C398.78,173.21,409.56,175.71,420.33,178.11C431.11,180.5,441.89,182.92,452.67,185.17C463.44,187.41,474.22,189.6,485,191.55C495.78,193.5,506.56,195.33,517.33,196.87C528.11,198.4,538.89,199.75,549.67,200.77C560.44,201.8,571.22,202.72,582,203.02C592.78,203.33,603.56,203.48,614.33,203.48C625.11,203.48,635.89,202.86,646.67,202.12C657.44,201.37,668.22,200.3,679,199.01C689.78,197.72,700.56,196.12,711.33,194.37C722.11,192.61,732.89,190.58,743.67,188.47C754.44,186.36,765.22,184.03,776,181.69C786.78,179.36,797.56,176.87,808.33,174.46C819.11,172.05,829.89,169.57,840.67,167.23C851.44,164.9,862.22,162.57,873,160.45C883.78,158.34,894.56,156.31,905.33,154.56C916.11,152.8,926.89,151.2,937.67,149.91C948.44,148.62,959.22,147.55,970,146.81C980.78,146.06,991.56,145.44,1002.33,145.44C1013.11,145.44,1023.89,145.6,1034.67,145.9C1045.44,146.21,1056.22,147.13,1067,148.15C1077.78,149.18,1088.56,150.52,1099.33,152.06C1110.11,153.59,1120.89,155.42,1131.67,157.37C1142.44,159.32,1153.22,161.52,1164,163.76C1174.78,166,1185.56,168.43,1196.33,170.82C1207.11,173.21,1217.89,175.71,1228.67,178.11C1239.44,180.5,1250.22,182.92,1261,185.17C1271.78,187.41,1282.56,189.6,1293.33,191.55C1304.11,193.5,1314.89,195.33,1325.67,196.87C1336.44,198.4,1347.22,199.75,1358,200.77C1368.78,201.8,1379.56,202.72,1390.33,203.02C1401.11,203.33,1411.89,203.48,1422.67,203.48C1433.44,203.48,1444.22,202.86,1455,202.12C1465.78,201.37,1476.56,200.3,1487.33,199.01C1498.11,197.72,1508.89,196.12,1519.67,194.37C1530.44,192.61,1541.22,190.58,1552,188.47C1562.78,186.36,1573.56,184.03,1584.33,181.69C1595.11,179.36,1605.89,176.91,1616.67,174.46';

const AREA_PATH = `${LINE_PATH}L1616.67,${BASE_HEIGHT}L-32.33,${BASE_HEIGHT}Z`;

/**
 * Animated wave shown while chart data (or the chart implementation itself)
 * loads. Fills its container via viewBox scaling.
 */
export const LoadingWave = ({ motion = true }: { motion?: boolean }) => {
  const prefersReducedMotion = useMedia('(prefers-reduced-motion: reduce)', false);

  return (
    <svg
      viewBox={`0 0 ${BASE_WIDTH} ${BASE_HEIGHT}`}
      preserveAspectRatio="none"
      style={{ width: '100%', height: '100%', display: 'block' }}
    >
      <defs>
        <linearGradient id="loading-wave-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={varMappings['token-gray-400']} stopOpacity={0.6} />
          <stop offset="100%" stopColor={varMappings['token-gray-400']} stopOpacity={0} />
        </linearGradient>
      </defs>
      <g>
        {motion && !prefersReducedMotion && (
          <animateTransform
            attributeName="transform"
            type="translate"
            from="0 0"
            to={`-${PERIOD_PX} 0`}
            dur={`${PERIOD_SECONDS}s`}
            repeatCount="indefinite"
          />
        )}
        <path
          d={LINE_PATH}
          fill="transparent"
          stroke={varMappings['token-gray-400']}
          strokeWidth={2}
          vectorEffect="non-scaling-stroke"
        />
        <path fill="url(#loading-wave-gradient)" d={AREA_PATH} />
      </g>
    </svg>
  );
};
